import { useNavigate } from 'react-router-dom';
import {
  FaHeadphones,
  FaPercent,
  FaPhoneAlt,
  FaSearch,
  FaShieldAlt,
  FaStar,
} from 'react-icons/fa';
import CruiseHeader from '../components/cruises/CruiseHeader';
import CruiseFooter from '../components/cruises/CruiseFooter';
import CruiseSearchWidget from '../components/cruises/CruiseSearchWidget';
import CruiseCta from '../components/cruises/CruiseCta';
import CruiseFixedCallBar from '../components/cruises/CruiseFixedCallBar';
import SectionHeader from '../components/home/SectionHeader';
import EnquiryForm from '../components/EnquiryForm';
import {
  CRUISE_DEALS,
  CRUISE_FEATURES,
  CRUISE_HERO_IMAGE,
  CRUISE_REVIEWS,
  TOP_CRUISE_REGIONS,
} from '../data/cruiseData';
import { CRUISE_PHONE_TEL } from '../constants/contact';
import { SITE_NAME } from '../constants/site';
import '../styles/home-page.css';
import '../styles/explore-page.css';
import '../styles/cruise-page.css';

const featureIcons = [FaHeadphones, FaPercent, FaShieldAlt, FaStar];

function OfferCard({ image, regionLabel, city, country, title, subtitle, price, priceSuffix, onBook }) {
  return (
    <article className="mcr-offer-card cruise-offer-card">
      <div className="mcr-offer-card__media cruise-offer-card__media">
        <img src={image} alt={`${regionLabel} cruise sailing from ${city}`} loading="lazy" />
        <span className="cruise-city-badge">{regionLabel}</span>
      </div>
      <div className="mcr-offer-card__body">
        <h3>{title}</h3>
        <p>{subtitle}</p>
        <div className="mcr-offer-card__footer cruise-offer-card__footer">
          <p className="mcr-offer-card__price">
            from <strong>${price}</strong>
            {priceSuffix}
          </p>
          <div className="cruise-offer-card__actions">
            <button type="button" className="cruise-offer-card__btn cruise-offer-card__btn--search" onClick={onBook}>
              <FaSearch aria-hidden /> Search
            </button>
            <a href={`tel:${CRUISE_PHONE_TEL}`} className="cruise-offer-card__btn cruise-offer-card__btn--call">
              <FaPhoneAlt aria-hidden /> Call
            </a>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function CruisePage() {
  const navigate = useNavigate();

  const goCruiseSearch = (port, destination) => {
    navigate(
      `/cruises/search?port=${encodeURIComponent(port)}&destination=${encodeURIComponent(destination)}&guests=2`
    );
  };

  return (
    <div className="cruise-page explore-page mcr-home" id="home">
      <CruiseHeader />

      <div className="mcr-hero-split">
        <section className="mcr-hero cruise-hero" aria-label="Cruise hero">
          <div className="mcr-hero__bg cruise-hero__bg" aria-hidden="true">
            <img src={CRUISE_HERO_IMAGE} alt="" className="cruise-hero__img" />
            <div className="cruise-hero__overlay" />
          </div>
          <div className="container mcr-hero__stack cruise-hero__stack">
            <h1 className="mcr-hero__title cruise-hero__title">
              Call our Cruise Experts for Unpublished Discounts — Save up to 30%
            </h1>
            <p className="mcr-hero__lead">
              Compare Caribbean, Mediterranean, Alaska & more — expert cabin advice 24/7.
            </p>
          </div>
        </section>

        <div className="mcr-hero-split__bridge" id="cruise-search">
          <div className="container mcr-hero-split__widget">
            <CruiseSearchWidget />
          </div>
        </div>

        <section className="mcr-section mcr-section--services mcr-section--compact" id="regions">
          <div className="container">
            <SectionHeader
              eyebrow="Trending now"
              title="Save more on top cruise regions"
              subtitle="Hand-picked sailings with exclusive member pricing and flexible change options."
              align="center"
            />
            <div className="cruise-regions-grid">
              {TOP_CRUISE_REGIONS.map((d) => (
                <article key={d.id} className="cruise-region-card">
                  <div className="cruise-region-card__media">
                    <img src={d.image} alt={`${d.region} cruises from ${d.city}`} loading="lazy" />
                    <span className="cruise-city-badge">{d.region}</span>
                    <span className="cruise-region-card__tag">Best value</span>
                  </div>
                  <div className="cruise-region-card__body">
                    <h3>{d.region}</h3>
                    <p className="cruise-region-card__port">
                      From {d.city}{d.country ? `, ${d.country}` : ''}
                    </p>
                    <p className="cruise-region-card__price">
                      from <strong>${d.price}</strong> /guest
                    </p>
                    <button
                      type="button"
                      className="cruise-region-card__cta"
                      onClick={() => goCruiseSearch(d.port, d.region)}
                    >
                      <FaSearch aria-hidden />
                      Search sailings
                    </button>
                  </div>
                </article>
              ))}
            </div>
            <CruiseCta
              variant="compact"
              className="explore-cta-after-services"
              title="Need help picking a sailing?"
              subtitle="Call our cruise team — available 24/7."
            />
          </div>
        </section>
      </div>

      <section className="cruise-cta-band">
        <div className="container">
          <CruiseCta
            variant="promo"
            title="Unpublished cruise discounts — save up to 30%"
            subtitle="Speak with a cruise expert for cabin upgrades, group rates, and last-minute sailings."
          />
        </div>
      </section>

      <section className="mcr-section mcr-section--compact" id="deals">
        <div className="container">
          <SectionHeader
            eyebrow="Featured sailings"
            title="Cruise deals updated daily"
            subtitle="Departure ports, regions, and guest counts — search above to compare."
          />
          <div className="cruise-deals-grid">
            {CRUISE_DEALS.map((c) => (
              <OfferCard
                key={c.id}
                image={c.image}
                regionLabel={c.destination}
                city={c.city}
                country={c.country}
                title={`${c.destination} cruise`}
                subtitle={`${c.line} · ${c.city} · ${c.nights} nights · ${c.dates}`}
                price={c.price}
                priceSuffix="/person"
                onBook={() => goCruiseSearch(c.port, c.destination)}
              />
            ))}
          </div>
          <CruiseCta
            variant="compact"
            title="Find your perfect cruise"
            subtitle="Caribbean, Mediterranean, Alaska & more — expert cabin advice."
          />
        </div>
      </section>

      <section className="mcr-section mcr-section--dark mcr-section--compact">
        <div className="container">
          <SectionHeader
            eyebrow="Why us"
            title={`Why travelers choose ${SITE_NAME} for cruises`}
            subtitle="Dedicated cruise concierge with transparent pricing."
            light
          />
          <div className="cruise-features-grid">
            {CRUISE_FEATURES.map((f, i) => {
              const Icon = featureIcons[i];
              return (
                <article key={f.title} className="mcr-feature">
                  <div className="mcr-feature__icon">
                    <Icon aria-hidden />
                  </div>
                  <h3>{f.title}</h3>
                  <p>{f.text}</p>
                </article>
              );
            })}
          </div>
          <CruiseCta
            variant="dark"
            title="Ready to book your cruise?"
            subtitle="One team, one phone call — cabin selection to final confirmation."
          />
        </div>
      </section>

      <section className="mcr-section mcr-section--soft mcr-section--compact">
        <div className="container">
          <SectionHeader
            eyebrow="Reviews"
            title="What cruise travelers say"
            subtitle="Real feedback from sailings booked through our concierge team."
            align="center"
          />
          <div className="cruise-reviews-grid">
            {CRUISE_REVIEWS.map((r) => (
              <article key={r.title} className="mcr-review-card">
                <div className="mcr-stars" aria-hidden>
                  {[1, 2, 3, 4, 5].map((s) => (
                    <FaStar key={s} size={12} />
                  ))}
                </div>
                <h3>&ldquo;{r.title}&rdquo;</h3>
                <p>{r.text}</p>
              </article>
            ))}
          </div>
          <CruiseCta
            variant="compact"
            className="cruise-reviews-cta"
            title="Join thousands of happy cruisers"
            subtitle="Call now for unpublished fares and cabin recommendations."
          />
        </div>
      </section>

      <section className="cruise-cta-band cruise-cta-band--final">
        <div className="container">
          <CruiseCta
            variant="promo"
            title="Book your cruise with expert support"
            subtitle="24/7 phone assistance — compare lines, cabins, and sailings in one call."
          />
        </div>
      </section>

      <section id="enquiry">
        <EnquiryForm />
      </section>

      <CruiseFooter />
      <CruiseFixedCallBar />
    </div>
  );
}
